import { SlPopup } from '@shoelace-style/shoelace';
import { LitElement, TemplateResult, css, html, nothing, svg } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { keyed } from 'lit/directives/keyed.js';
import { styleMap } from 'lit/directives/style-map.js';
import { Disposable, disposeAll, onDOMEvent } from '../utils.js';

export type FKGuideArea = {
  // Called on every measurement, since the target may render after the guide opens.
  target: () => Element | null | undefined,
  title: string,
  description: TemplateResult,
  // A docs page, shown as a "Learn more" link at the bottom of the card.
  href?: string,
  placement?: SlPopup['placement'],
};

// Space between a target and the edge of its hole. Controls often sit ~10px apart;
// a small padding keeps a strip of shade between their holes.
const HOLE_PADDING = 3;
const HOLE_RADIUS = 6;
const MAX_NUMBER_SIZE = 48;
// A tall hole, like a long table, keeps its number near the top so it shows without scrolling.
const MAX_NUMBER_OFFSET = 200;
// Visible space between a hole and its card.
const CARD_GAP = 8;
const FADE_MS = 150;
// The whole overlay fades in a little slower than a single area.
const OPEN_FADE_MS = 250;

/**
 * A "Guide" button that opens a guide to the page. Put it where the button should go.
 *
 * The guide dims the page and cuts a numbered, half-shaded hole over the target of every area.
 * Hovering, focusing or clicking a hole clears its shade and shows a card that
 * explains it. The page under the guide is inert.
 * Escape, a click on the dimmed part of the page, or the Close button close the guide.
 *
 * The overlay covers the whole page and uses page coordinates, so it scrolls
 * together with the page. Holes are measured again only when the layout changes.
 *
 *   <fk-guide .areas=${[{
 *     target: () => this._branchSelector,
 *     title: 'Branch',
 *     description: html`<p>Test results come from the selected branch.</p>`,
 *   }]}></fk-guide>
 */
@customElement('fk-guide')
export class FKGuide extends LitElement {
  @property({ attribute: false }) areas: FKGuideArea[] = [];
  @property() label = 'Page guide';

  @state() private _open = false;
  // In page coordinates.
  @state() private _holes: (DOMRect | undefined)[] = [];
  @state() private _active?: number;
  // A tapped hole keeps its card open until another hole takes over.
  private _pinned = false;

  @query('dialog') private _dialog?: HTMLDialogElement;
  @query('sl-popup') private _popup?: SlPopup;
  @query('.card-area') private _cardArea?: HTMLElement;

  private _listeners: Disposable[] = [];
  private _resizeObserver?: ResizeObserver;
  private _measureFrame?: number;

  // The card reads the active hole every time it repositions.
  private _cardAnchor = {
    getBoundingClientRect: () => this._cardAnchorRect(),
  };

  async show() {
    if (this._open)
      return;
    this._open = true;
    await this.updateComplete;
    this._dialog?.showModal();
    // Focusing the dialog itself instead of its first button avoids a focus ring on
    // Close; Tab still reaches Close first.
    this._dialog?.focus();
    this._measure();

    // Targets move when data finishes loading or the window resizes.
    // A target that grows or shrinks moves the ones below it.
    this._resizeObserver = new ResizeObserver(() => this._scheduleMeasure());
    for (const area of this.areas) {
      const target = area.target();
      if (target)
        this._resizeObserver.observe(target);
    }
    this._listeners = [
      onDOMEvent(window, 'resize', () => this._scheduleMeasure()),
    ];
  }

  hide() {
    this._dialog?.close();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this._stopTracking();
    this._open = false;
  }

  override updated() {
    if (this._active !== undefined)
      this._popup?.reposition();
  }

  private _onClose() {
    this._stopTracking();
    this._open = false;
    this._active = undefined;
    this._pinned = false;
  }

  private _stopTracking() {
    disposeAll(this._listeners);
    this._resizeObserver?.disconnect();
    this._resizeObserver = undefined;
    if (this._measureFrame !== undefined)
      cancelAnimationFrame(this._measureFrame);
    this._measureFrame = undefined;
  }

  private _scheduleMeasure() {
    if (this._measureFrame !== undefined)
      return;
    this._measureFrame = requestAnimationFrame(() => {
      this._measureFrame = undefined;
      this._measure();
    });
  }

  private _measure() {
    const { scrollX, scrollY } = window;
    const holes = this.areas.map(area => {
      const rect = area.target()?.getBoundingClientRect();
      if (!rect || !rect.width || !rect.height)
        return undefined;
      return new DOMRect(rect.x + scrollX - HOLE_PADDING, rect.y + scrollY - HOLE_PADDING, rect.width + 2 * HOLE_PADDING, rect.height + 2 * HOLE_PADDING);
    });
    if (holes.length !== this._holes.length || holes.some((hole, index) => !isSameRect(hole, this._holes[index])))
      this._holes = holes;

    // The overlay covers the whole page. It adds to the page's scroll height itself,
    // so it collapses while the page is measured; both happen before the next paint.
    const dialog = this._dialog;
    if (dialog) {
      dialog.style.height = '0';
      dialog.style.height = `${document.documentElement.scrollHeight}px`;
    }
  }

  // A hole taller than half the viewport leaves no room for the card above or below it.
  // The card then goes to the top edge of the hole's visible part.
  private _cardAnchorRect(): DOMRect {
    const hole = this._active !== undefined ? this._holes[this._active] : undefined;
    if (!hole)
      return new DOMRect();
    // The card positions against the viewport. Holes of full-width controls stick out
    // past the viewport by their padding; clamping keeps the card's alignment from flipping.
    const { clientWidth: viewportWidth, clientHeight: viewportHeight } = document.documentElement;
    const top = hole.top - window.scrollY;
    const bottom = hole.bottom - window.scrollY;
    const left = Math.max(hole.left - window.scrollX, 0);
    const right = Math.min(hole.right - window.scrollX, viewportWidth);
    const visibleTop = Math.max(top, 0);
    const visibleBottom = Math.min(bottom, viewportHeight);
    if (visibleBottom - visibleTop > viewportHeight / 2)
      return new DOMRect(left, visibleTop, right - left, 0);
    return new DOMRect(left, top, right - left, bottom - top);
  }

  private _showCard(index: number, pinned: boolean) {
    this._active = index;
    this._pinned = pinned;
  }

  private _hideCard() {
    if (!this._pinned)
      this._active = undefined;
  }

  private _isInCard(target: EventTarget | null) {
    return target instanceof Node && !!this._cardArea?.contains(target);
  }

  private _isActiveHole(target: EventTarget | null) {
    return target instanceof Element && !!target.closest('.hole[active]');
  }

  // Touch has no hover: there, the card opens on tap only.
  private _onHoleEnter(event: PointerEvent, index: number) {
    if (event.pointerType === 'mouse' && this._active !== index)
      this._showCard(index, false);
  }

  // The card area touches its hole, so the pointer can move between them
  // without crossing the shade.
  private _onHoleLeave(event: PointerEvent) {
    if (event.pointerType === 'mouse' && !this._isInCard(event.relatedTarget))
      this._hideCard();
  }

  private _onCardLeave(event: PointerEvent) {
    if (event.pointerType === 'mouse' && !this._isActiveHole(event.relatedTarget))
      this._hideCard();
  }

  // Mouse users see the card on hover. A tap or a key press keeps it open.
  private _onHoleClick(event: MouseEvent, index: number) {
    this._showCard(index, (event as PointerEvent).pointerType !== 'mouse');
  }

  private _onHoleFocus(event: FocusEvent, index: number) {
    // Mouse clicks focus the hole too; only keyboard focus should open the card.
    if ((event.target as HTMLElement).matches(':focus-visible'))
      this._showCard(index, false);
  }

  private _onHoleBlur(event: FocusEvent, index: number) {
    if (this._active === index && !this._isInCard(event.relatedTarget))
      this._hideCard();
  }

  private _onCardFocusOut(event: FocusEvent) {
    if (!this._isInCard(event.relatedTarget) && !this._isActiveHole(event.relatedTarget))
      this._hideCard();
  }

  // Clicks on the dimmed area land on the dialog itself.
  private _onDialogClick(event: MouseEvent) {
    if (event.target === this._dialog)
      this.hide();
  }

  override render() {
    return html`
      <button type=button class=trigger aria-haspopup=dialog @click=${() => this.show()}>
        <sl-icon name=compass></sl-icon>
        Guide
      </button>
      <dialog
        tabindex=-1
        aria-label=${this.label}
        @click=${this._onDialogClick}
        @close=${this._onClose}
      >${this._open ? this._renderGuide() : nothing}</dialog>
    `;
  }

  private _renderGuide() {
    const active = this._active !== undefined ? this.areas[this._active] : undefined;
    return html`
      <svg class=shade aria-hidden=true>
        <defs>
          <mask id=holes>
            <rect width=100% height=100% fill=white></rect>
            ${this._holes.map(hole => hole ? svg`
              <rect x=${hole.x} y=${hole.y} width=${hole.width} height=${hole.height} rx=${HOLE_RADIUS} fill=black></rect>
            ` : nothing)}
          </mask>
        </defs>
        <rect class=outside width=100% height=100% mask="url(#holes)"></rect>
        <!-- Holes carry a lighter shade of their own, which fades out on hover. -->
        ${this._holes.map((hole, index) => hole ? svg`
          <rect
            class=inside
            ?active=${index === this._active}
            x=${hole.x} y=${hole.y} width=${hole.width} height=${hole.height} rx=${HOLE_RADIUS}
          ></rect>
        ` : nothing)}
      </svg>
      <div class=intro>
        <span>Hover over a numbered area to see what it does.</span>
        <button type=button class=close @click=${() => this.hide()}>Close</button>
      </div>
      ${this._holes.map((hole, index) => hole ? html`
        <button
          type=button
          class=hole
          ?active=${index === this._active}
          style=${styleMap({
            left: `${hole.x}px`,
            top: `${hole.y}px`,
            width: `${hole.width}px`,
            height: `${hole.height}px`,
          })}
          aria-label=${this.areas[index].title}
          aria-expanded=${index === this._active}
          @pointerenter=${(event: PointerEvent) => this._onHoleEnter(event, index)}
          @pointerleave=${this._onHoleLeave}
          @focus=${(event: FocusEvent) => this._onHoleFocus(event, index)}
          @blur=${(event: FocusEvent) => this._onHoleBlur(event, index)}
          @click=${(event: MouseEvent) => this._onHoleClick(event, index)}
        ><span class=number>${index + 1}</span></button>
      ` : nothing)}
      <!-- Absolute positioning places the card in page coordinates, so it scrolls with its hole. -->
      <sl-popup
        strategy=absolute
        placement=${active?.placement ?? 'bottom-start'}
        flip
        shift
        shift-padding=8
        .anchor=${this._cardAnchor}
        ?active=${!!active}
      >
        ${active ? keyed(this._active, html`
          <div
            class=card-area
            @pointerleave=${this._onCardLeave}
            @focusout=${this._onCardFocusOut}
          >
            <div class=card>
              <div class=title>${active.title}</div>
              ${active.description}
              ${active.href ? html`
                <a class=learn-more href=${active.href} target=_blank rel=noopener>
                  Learn more <sl-icon library=boxicons name=bx-link-external></sl-icon>
                </a>
              ` : nothing}
            </div>
          </div>
        `) : nothing}
      </sl-popup>
    `;
  }

  static styles = css`
    /* Only the button takes room in the page layout; the open dialog lives in the top layer. */
    :host {
      display: inline-flex;
      vertical-align: middle;
    }

    /* Gray like fk-help's (?) icon, since it's help for the whole page. */
    .trigger {
      display: inline-flex;
      align-items: center;
      gap: var(--sl-spacing-2x-small);
      padding: var(--sl-spacing-3x-small) var(--sl-spacing-2x-small);
      border: none;
      border-radius: var(--sl-border-radius-medium);
      background: none;
      color: var(--sl-color-neutral-500);
      font-family: var(--sl-font-sans);
      font-size: var(--sl-font-size-small);
      cursor: pointer;

      &:hover {
        color: var(--sl-color-neutral-700);
      }

      &:focus-visible {
        outline: var(--sl-focus-ring);
        outline-offset: 2px;
      }
    }

    /* An absolutely positioned top-layer element sits in page coordinates:
       it starts at the top of the page and scrolls with it. _measure() sets its height. */
    dialog {
      position: absolute;
      inset: 0 auto auto 0;
      box-sizing: border-box;
      width: 100%;
      max-width: none;
      max-height: none;
      margin: 0;
      padding: 0;
      border: none;
      background: none;
      overflow: hidden;
      outline: none;

      &::backdrop {
        background: none;
      }
    }

    .shade {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;

      .outside {
        fill: black;
        fill-opacity: 0.55;
      }

      .inside {
        fill: black;
        fill-opacity: 0.3;

        &[active] {
          fill-opacity: 0;
        }
      }
    }

    .hole {
      position: absolute;
      box-sizing: border-box;
      margin: 0;
      padding: 0;
      border: none;
      border-radius: ${HOLE_RADIUS}px;
      background: none;
      cursor: help;
      /* Lets the number size itself by the hole's height. */
      container-type: size;
      /* A blurry white border marks the area while it's shaded. */
      box-shadow:
        0 0 8px 1px rgb(255 255 255 / 0.8),
        inset 0 0 8px 1px rgb(255 255 255 / 0.8);

      /* Same shadows, fully transparent, so the border fades instead of popping. */
      &[active] {
        box-shadow:
          0 0 8px 1px rgb(255 255 255 / 0),
          inset 0 0 8px 1px rgb(255 255 255 / 0);
      }

      &:focus-visible {
        outline: 2px solid var(--sl-color-primary-500);
        outline-offset: 2px;
      }
    }

    /* The number sits in the middle of its hole and grows with it, up to a limit. */
    .number {
      position: absolute;
      top: min(50%, ${MAX_NUMBER_OFFSET}px);
      left: 50%;
      transform: translate(-50%, -50%);
      font-size: min(70cqh, ${MAX_NUMBER_SIZE}px);
      color: white;
      font-family: var(--sl-font-sans);
      font-weight: var(--sl-font-weight-bold);
      line-height: 1;
      text-shadow: 0 0 8px rgb(0 0 0 / 0.6);
      pointer-events: none;
    }

    .hole[active] .number {
      opacity: 0;
    }

    /* The guide fades in when it opens. Hovering an area fades it open and fades its card in. */
    @media (prefers-reduced-motion: no-preference) {
      dialog[open] {
        animation: fade-in ${OPEN_FADE_MS}ms ease-out;
      }

      .shade .inside {
        transition: fill-opacity ${FADE_MS}ms ease-out;
      }

      .hole {
        transition: box-shadow ${FADE_MS}ms ease-out;
      }

      .number {
        transition: opacity ${FADE_MS}ms ease-out;
      }

      .card {
        animation: fade-in ${FADE_MS}ms ease-out;
      }
    }

    @keyframes fade-in {
      from {
        opacity: 0;
      }
    }

    /* The intro stays on screen while the page scrolls. */
    .intro {
      position: fixed;
      z-index: 1;
      top: var(--sl-spacing-medium);
      left: 50%;
      transform: translateX(-50%);
      box-sizing: border-box;
      max-width: calc(100vw - 2 * var(--sl-spacing-medium));
      display: flex;
      align-items: center;
      gap: var(--sl-spacing-medium);
      padding: var(--sl-spacing-x-small) var(--sl-spacing-x-small) var(--sl-spacing-x-small) var(--sl-spacing-large);
      /* White on a darker patch of the shade, like the numbers. The blur keeps
         whatever sits underneath, like the header's repository link, out of the way. */
      border-radius: var(--sl-border-radius-pill);
      background: rgb(0 0 0 / 0.6);
      backdrop-filter: blur(6px);
      color: white;
      font-family: var(--sl-font-sans);
      font-size: var(--sl-font-size-medium);
      font-weight: var(--sl-font-weight-semibold);
      white-space: nowrap;
    }

    .close {
      padding: var(--sl-spacing-2x-small) var(--sl-spacing-medium);
      border: 1px solid rgb(255 255 255 / 0.8);
      border-radius: var(--sl-border-radius-pill);
      background: none;
      color: inherit;
      font: inherit;
      font-size: var(--sl-font-size-small);
      cursor: pointer;

      &:hover {
        background: rgb(255 255 255 / 0.15);
      }

      &:focus-visible {
        outline: 2px solid white;
        outline-offset: 2px;
      }
    }

    /* Stacking: holes, then the intro, then the card that's being read. The intro comes
       first in the markup so that keyboard focus reaches Close first. */
    sl-popup::part(popup) {
      z-index: 2;
    }

    /* The card area reaches all the way to its hole, and its padding on that side
       draws the gap. sl-popup reports the side it picked after flipping. */
    sl-popup[data-current-placement^=top] .card-area {
      padding-bottom: ${CARD_GAP}px;
    }

    sl-popup[data-current-placement^=bottom] .card-area {
      padding-top: ${CARD_GAP}px;
    }

    sl-popup[data-current-placement^=left] .card-area {
      padding-right: ${CARD_GAP}px;
    }

    sl-popup[data-current-placement^=right] .card-area {
      padding-left: ${CARD_GAP}px;
    }

    /* A plain white panel: the shade already sets it apart from the page.
       The shadow separates it from the clear area it may overlap.
       The card resets everything it could inherit from the place the guide is used in. */
    .card {
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      gap: var(--sl-spacing-x-small);
      width: max-content;
      max-width: min(24rem, calc(100vw - 16px));
      padding: var(--sl-spacing-medium) var(--sl-spacing-large);
      border-radius: var(--sl-border-radius-large);
      background: var(--sl-color-neutral-0);
      box-shadow: var(--sl-shadow-large);
      color: var(--sl-color-neutral-700);
      font-family: var(--sl-font-sans);
      font-size: var(--sl-font-size-medium);
      font-weight: var(--sl-font-weight-normal);
      font-style: normal;
      line-height: 1.5;
      letter-spacing: normal;
      text-align: start;
      text-transform: none;
      white-space: normal;
      cursor: auto;
    }

    .card > * {
      margin: 0;
    }

    .title {
      color: var(--sl-color-neutral-900);
      font-size: var(--sl-font-size-large);
      font-weight: var(--sl-font-weight-semibold);
      line-height: var(--sl-line-height-dense);
    }

    .learn-more {
      align-self: flex-start;
      display: inline-flex;
      align-items: center;
      gap: var(--sl-spacing-2x-small);
      color: var(--sl-color-primary-600);
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  `;
}

function isSameRect(a: DOMRect | undefined, b: DOMRect | undefined) {
  if (!a || !b)
    return a === b;
  return a.x === b.x && a.y === b.y && a.width === b.width && a.height === b.height;
}
